// Conversation history display for issue detail
import { useRef, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { MessageSquare } from 'lucide-react'

/** Strip JSON, code blocks, and file attachment metadata from messages */
function stripMetadataFromContent(text: string): string {
  let cleaned = text
  // Remove all fenced code blocks (```json...```, ```sql...```, etc.)
  cleaned = cleaned.replace(/```[\s\S]*?```/g, '')
  // Remove JSON objects with agent metadata keys
  cleaned = cleaned.replace(/\{[^{}]*"(?:extractedData|completeness|nextAction|suggestedQuickReplies|problemTitle|problemDescription)"[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g, '')
  // Remove trailing JSON blocks
  cleaned = cleaned.replace(/\n\s*\{(?:\s*"[a-zA-Z]+"[\s\S]*?)\}\s*$/g, '')
  // Remove file attachment blocks (--- ATTACHED DOCUMENTS --- ... --- END DOCUMENTS ---)
  cleaned = cleaned.replace(/---\s*ATTACHED DOCUMENTS\s*---[\s\S]*?---\s*END DOCUMENTS\s*---/g, '')
  // Remove image attachment blocks
  cleaned = cleaned.replace(/\[ATTACHED IMAGE:[^\]]*\][\s\S]*?(?=\n\n|\n[A-Z]|$)/g, '')
  return cleaned.trim()
}

interface Conversation {
  id: string
  role: string
  content: string
  user_id: string | null
  agent_type: string | null
  metadata: unknown
  created_at: string
}

interface IssueConversationListProps {
  conversations: Conversation[]
}

export function IssueConversationList({ conversations }: IssueConversationListProps): JSX.Element {
  const bottomRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [conversations.length])

  if (conversations.length === 0) {
    return (
      <Card className="border-kaycoo-teal-light bg-kaycoo-white">
        <CardContent className="py-6 text-center text-sm text-kaycoo-charcoal-2">
          No conversation history yet.
        </CardContent>
      </Card>
    )
  }

  const getAgentBadge = (msg: Conversation) => {
    const meta = msg.metadata as Record<string, unknown> | null
    if (msg.role === 'user') return { label: 'User', className: '' }
    if (msg.role === 'system') return { label: 'System', className: 'bg-kaycoo-white-2' }
    if (meta?.expert_label) return { label: String(meta.expert_label), className: 'border-purple-400 text-purple-700' }
    if (meta?.agent_type === 'domain_expert') return { label: 'Domain Expert', className: 'border-purple-400 text-purple-700' }
    return { label: 'AI Assistant', className: '' }
  }

  return (
    <Card className="border-kaycoo-teal-light bg-kaycoo-white">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm text-kaycoo-teal-dark">
          <MessageSquare className="h-4 w-4" />
          Conversation ({conversations.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {conversations.map((msg) => {
          const badge = getAgentBadge(msg)
          const displayContent = msg.role === 'user' ? msg.content : stripMetadataFromContent(msg.content)
          // Skip empty messages after stripping
          if (!displayContent) return null
          return (
            <div key={msg.id}
              className={`rounded-lg p-3 text-sm ${
                msg.role === 'user'
                  ? 'ml-8 bg-kaycoo-teal-pale text-kaycoo-teal-dark'
                  : msg.role === 'system'
                    ? 'bg-kaycoo-white-2 text-kaycoo-charcoal-2 italic'
                    : 'mr-8 bg-kaycoo-white-2 text-kaycoo-charcoal'
              }`}>
              <div className="mb-1 flex items-center justify-between">
                <Badge variant="outline" className={`text-xs ${badge.className}`}>
                  {badge.label}
                </Badge>
                <span className="text-xs text-kaycoo-charcoal-2">
                  {new Date(msg.created_at).toLocaleString()}
                </span>
              </div>
              <p className="whitespace-pre-wrap">{displayContent}</p>
            </div>
          )
        })}
        <div ref={bottomRef} />
      </CardContent>
    </Card>
  )
}
